import {
    OrderedTransactions,
    TransactionResult,
    ParallelTransactionsBatch,
    SendTransactionsInterface,
    SendableTransactionWithIdentifier,
    TransactionType,
    isVersionedTransaction,
    serializeAndEncodeTransaction,
    SendableParallelTransactionsBatch,
    useTransactionsState,
    BundleOptions
} from "@bridgesplit/react";
import {
    Result,
    ErrorType,
    SendableTransaction,
    TransactionStatus,
    getReadableErrorMessage,
    parseErrorFromOptions,
    sleep,
    base64,
    simulateTransactionAndSendAlert,
    sendTransactionAnalytics,
    IS_LOCAL_NX_DEV
} from "@bridgesplit/utils";
import { getSignInTransaction } from "@bridgesplit/abf-sdk";

import { AuthSliceState, useSendTransactionsMutation } from "../reducers";
import { TransactionSenderOptions, TransactionWalletAuth } from "../types";
import {
    MAX_TRANSACTION_BUNDLE_SIZE,
    TRANSACTIONS_STATUS_ROUTE,
    TRANSACTION_DEFAULT_BATCH_COMMITMENT,
    WALLET_ERROR_MESSAGES,
    WALLET_ERROR_NAME_MAPPING
} from "../constants";
import { axiosCreateMarkets } from "../reducers/util";
import { getTransactionHeadersFromCookies } from "./auth";
import { LoopscaleWallet } from "../wallet";

const SEND_JITO_BUNDLES_SEQUENTIALLY_FOR_DEBUG = false;

/**
 * Send transactions using the ABF sender API. Ideally not exposed
 * @param transactionWallet either MPC wallet identifier or a browser wallet (Loopscale Wallet)
 * If browser wallet, transaction is signed in `useSignAndSendTransactions`
 * Else, transactions are signed server-side using MPC
 */
export function useSendTransactions() {
    const sendSignedTransactions = useSendSignedTransactions();
    const signTransactions = useSignTransactions();
    const { setSignaturePending } = useTransactionsState();

    async function sendTransactions(
        transactionWallet: TransactionWalletAuth,
        orderedTransactions: OrderedTransactions,
        options: TransactionSenderOptions
    ): Promise<Result<TransactionResult[]>> {
        try {
            const {
                refetch,
                onFail,
                onSuccess,
                onSign,
                skipSignature,
                delayMethod = "geyser",
                minGeyserConfirmations = 2
            } = options;

            const transactionBatches = orderedTransactions;

            let signedBatches = transactionBatches;
            if (!skipSignature) {
                const signedRes = await signTransactions(transactionBatches, transactionWallet, options);
                if (!signedRes.isOk()) {
                    return Result.err(signedRes);
                }
                signedBatches = signedRes.unwrap();
            }

            onSign?.();

            const sendableTransactions = prepareTransactionBatchesToSend(signedBatches, transactionWallet, options);
            if (!sendableTransactions.isOk()) return Result.err(sendableTransactions);

            const preparedTransactions = sendableTransactions.unwrap();

            const statusesRes = await sendSignedTransactions(preparedTransactions);

            if (!statusesRes.isOk()) {
                // explicitly catch sign error in case MPC cookies are out of date

                return Result.err(statusesRes);
            }

            const statuses = statusesRes.unwrapOr([]);

            if (statuses.length === 0) {
                return Result.errFromMessage("No transactions were sent", {
                    errorType: ErrorType.TransactionSendError
                });
            }

            const errors = statuses.filter((s) => s.status !== TransactionStatus.Confirmed);
            if (errors.length) {
                void sendFailureSimulationAlerts(transactionWallet, signedBatches, statuses);
                await onFail?.();
            } else {
                if (delayMethod === "geyser") {
                    await pollUntilGeyserConfirmed(
                        statuses.map((s) => s.signature),
                        minGeyserConfirmations
                    );
                }
                if (delayMethod === "sleep") {
                    await sleep(2_500);
                }

                void sendTransactionSuccessAnalyticsFromBatch(transactionWallet, signedBatches, statuses);
                await onSuccess?.();
            }
            await refetch?.();

            return Result.ok(statuses);
        } catch (error) {
            setSignaturePending(false);
            return Result.err(error);
        }
    }

    return async (
        transactionWallet: TransactionWalletAuth,
        orderedTransactions: OrderedTransactions,
        options: TransactionSenderOptions
    ) => {
        const res = await sendTransactions(transactionWallet, orderedTransactions, options);

        if (!res.isOk()) {
            options.onFail?.();
        }
        return res;
    };
}

/**
 * Sign and send transactions using the browser wallet's signAndSendTransactions.
 * Only supports browser wallet (LoopscaleWallet with signAndSendTransactions).
 * Does not use the backend send pipeline; signatures are returned immediately (no confirmation polling).
 */
export function useSignAndSendTransactions() {
    const { setSignaturePending } = useTransactionsState();

    return async (
        transactionWallet: TransactionWalletAuth,
        orderedTransactions: OrderedTransactions,
        options: TransactionSenderOptions
    ): Promise<Result<TransactionResult[]>> => {
        if (!("wallet" in transactionWallet)) {
            const err = Result.errFromMessage("Wallet not connected", {
                errorType: ErrorType.TransactionSendError
            });
            options.onFail?.();
            return Result.err(err);
        }

        const { refetch, onFail, onSuccess, delayMethod } = options;
        const wallet = transactionWallet.wallet;

        const flat = orderedTransactions.flatMap((batch) => batch.transactions);
        const transactions = flat.map((t) => t.transaction);
        const identifiers = flat.map((t) => t.identifier);

        setSignaturePending(true);
        let res: Result<TransactionResult[]>;
        try {
            const signatures = await wallet.signAndSendTransactions(transactions);
            if (signatures.length !== identifiers.length) {
                res = Result.errFromMessage("Result length mismatch", {
                    errorType: ErrorType.TransactionSendError
                });
            } else {
                if (delayMethod === "sleep") {
                    // sign and send from wallet is fast.
                    await sleep(3_000);
                }
                const transactionResults: TransactionResult[] = signatures.map((s, i) => ({
                    signature: s.signature,
                    status: TransactionStatus.Confirmed,
                    identifier: identifiers[i]
                }));
                await onSuccess?.();
                await refetch?.();
                res = Result.ok(transactionResults);
            }
        } catch (error) {
            res = Result.errWithDebug(getReadableErrorMessage("sign and send transactions"), error);
        } finally {
            setSignaturePending(false);
        }

        if (!res.isOk()) {
            onFail?.();
        }
        return res;
    };
}

// Helper: Extract wallet address from transaction wallet auth
function getWalletAddress(transactionWallet: TransactionWalletAuth): string | undefined {
    return "wallet" in transactionWallet
        ? transactionWallet.wallet.publicKey.toBase58()
        : transactionWallet.mpcIdentifier;
}

async function sendFailureSimulationAlerts(
    transactionWallet: TransactionWalletAuth,
    batches: ParallelTransactionsBatch[],
    statuses: TransactionResult[]
) {
    try {
        const walletAddress = getWalletAddress(transactionWallet);
        if (!walletAddress) return;

        const failedIdentifiers = new Set(
            statuses.filter((s) => s.status !== TransactionStatus.Confirmed).map((s) => s.identifier)
        );
        if (!failedIdentifiers.size) return;

        for (const batch of batches) {
            for (const { transaction, identifier, analyticsInfo } of batch.transactions) {
                if (!failedIdentifiers.has(identifier)) continue;

                await simulateTransactionAndSendAlert({
                    transaction,
                    user: walletAddress,
                    analyticsInfo: analyticsInfo
                        ? {
                              ...analyticsInfo,
                              transactionIdentifier: identifier
                          }
                        : undefined
                });
            }
        }
    } catch (error) {
        Result.err(error, { skipSentry: true });
    }
}

async function sendTransactionSuccessAnalyticsFromBatch(
    transactionWallet: TransactionWalletAuth,
    batches: ParallelTransactionsBatch[],
    statuses: TransactionResult[]
) {
    try {
        const walletAddress = getWalletAddress(transactionWallet);
        if (!walletAddress) return;

        const successfulTransactions = statuses.filter((s) => s.status === TransactionStatus.Confirmed);
        if (!successfulTransactions.length) return;

        for (const batch of batches) {
            for (const { analyticsInfo, identifier } of batch.transactions) {
                const txStatus = successfulTransactions.find((s) => s.identifier === identifier);
                if (!txStatus || !analyticsInfo?.transactionType) continue;

                sendTransactionAnalytics(walletAddress, {
                    ...analyticsInfo,
                    transactionStatus: TransactionStatus.Confirmed,
                    simulation: false,
                    transactionIdentifier: txStatus.signature
                });
            }
        }
    } catch (error) {
        Result.err(error, { skipSentry: true });
    }
}

export function useSignTransactions() {
    const { setSignaturePending } = useTransactionsState();

    return async function signTransactions(
        batches: ParallelTransactionsBatch[],
        transactionWallet: TransactionWalletAuth,
        options: TransactionSenderOptions
    ): Promise<Result<ParallelTransactionsBatch[]>> {
        setSignaturePending(true);

        const result = await (async (): Promise<Result<ParallelTransactionsBatch[]>> => {
            const flattenedTransactions: SendableTransaction[] = [];
            const flattenedIdentifiers: string[] = [];

            batches.forEach((txns) => {
                txns.transactions.forEach(({ transaction, identifier }) => {
                    flattenedTransactions.push(transaction);
                    flattenedIdentifiers.push(identifier);
                });
            });

            const signedBatches = [...batches];
            let signedTransactions: SendableTransaction[] = [];
            if ("wallet" in transactionWallet) {
                const signedTransactionsRes = await walletSignTransactions(
                    transactionWallet.wallet,
                    flattenedTransactions
                );
                if (!signedTransactionsRes.isOk()) return Result.err(signedTransactionsRes);
                signedTransactions = signedTransactionsRes.unwrap();
                let flatIndex = 0;
                signedBatches.forEach((txns) => {
                    txns.transactions.forEach((_, index) => {
                        if (signedTransactions[flatIndex]) {
                            txns.transactions[index].transaction = signedTransactions[flatIndex];
                            flatIndex++;
                        }
                    });
                });
            }

            return Result.ok(signedBatches);
        })();
        setSignaturePending(false);

        return result;
    };
}

async function walletSignTransactions(
    wallet: LoopscaleWallet,
    flattenedTransactions: SendableTransaction[]
): Promise<Result<SendableTransaction[]>> {
    try {
        const signed = await wallet.signAllTransactions(flattenedTransactions);
        return Result.ok(signed);
    } catch (error) {
        const walletAddress = wallet.walletAddress;

        if (error instanceof Error) {
            if (error.message) {
                const parsedError = parseErrorFromOptions(error.message, WALLET_ERROR_NAME_MAPPING);
                if (parsedError) return Result.errFromMessage(parsedError, { skipSentry: true });
            }

            if (error.name in WALLET_ERROR_MESSAGES) {
                return Result.errFromMessage(WALLET_ERROR_MESSAGES[error.name], { skipSentry: true });
            }
        }
        return Result.errWithDebug(getReadableErrorMessage("sign transactions"), error, {
            wallet: walletAddress,
            walletClientType: wallet.walletClientType
        });
    }
}

function useSendSignedTransactions() {
    const [sendTransactions] = useSendTransactionsMutation();
    return async (transactions: SendTransactionsInterface): Promise<Result<TransactionResult[]>> => {
        try {
            const txnResult = await sendTransactions(transactions);
            if ("error" in txnResult) {
                return Result.errWithDebug(getReadableErrorMessage("send transactions"), txnResult.error);
            }
            return Result.ok(txnResult.data);
        } catch (error) {
            return Result.errWithDebug(getReadableErrorMessage("send authenticated transactions"), error);
        }
    };
}

function prepareTransactionBatchesToSend(
    batches: ParallelTransactionsBatch[],
    transactionWallet: TransactionWalletAuth,
    options: TransactionSenderOptions
): Result<SendTransactionsInterface> {
    const batchesToSend = SEND_JITO_BUNDLES_SEQUENTIALLY_FOR_DEBUG
        ? splitBundleBatchesForSequentialDebug(batches)
        : batches;

    const encodedBatches: SendableParallelTransactionsBatch[] = batchesToSend.map((batch, i) => {
        const defaultConfirmation =
            batchesToSend.length > 1 && i !== batchesToSend.length - 1
                ? TRANSACTION_DEFAULT_BATCH_COMMITMENT
                : "confirmed";
        const bundleOption = resolveBundleOption(batch, options);

        return {
            transactions: batch.transactions.map(({ identifier, transaction }): SendableTransactionWithIdentifier => ({
                identifier,
                transaction: serializeAndEncodeTransaction(transaction),
                transactionType: isVersionedTransaction(transaction) ? TransactionType.V0 : TransactionType.Legacy
            })),
            allowFailure: options.allowBatchFailure ?? batch.allowFailure,
            commitmentLevel: options.commitmentLevel ?? batch.commitmentLevel ?? defaultConfirmation,
            bundle: bundleOption
        };
    });

    // it's better to err rather than sending non atomically (could lead to edge cases)
    const bundleSizeExceeded =
        encodedBatches.some((b) => b.bundle === BundleOptions.BundleOnly) &&
        batchesToSend.some((b) => b.transactions.length > MAX_TRANSACTION_BUNDLE_SIZE);
    if (bundleSizeExceeded) {
        return Result.errFromMessage("Unable to send. Try reducing the amount of data");
    }

    return Result.ok({
        batches: encodedBatches,
        mpcIdentifier: "mpcIdentifier" in transactionWallet ? transactionWallet.mpcIdentifier : undefined
    });
}

function splitBundleBatchesForSequentialDebug(batches: ParallelTransactionsBatch[]): ParallelTransactionsBatch[] {
    return batches.flatMap((batch) => {
        if (batch.bundle !== BundleOptions.BundleOnly || batch.transactions.length <= 1) return [batch];

        if (IS_LOCAL_NX_DEV) {
            // eslint-disable-next-line no-console
            console.debug("[txn-sender] sending bundle batch sequentially for debug", {
                txCount: batch.transactions.length,
                identifiers: batch.transactions.map((transaction) => transaction.identifier)
            });
        }

        return batch.transactions.map((transaction) => ({
            ...batch,
            transactions: [transaction],
            bundle: BundleOptions.Priority
        }));
    });
}

function resolveBundleOption(batch: ParallelTransactionsBatch, options: TransactionSenderOptions): BundleOptions {
    const resolved = (() => {
        if (batch.bundle !== undefined) return batch.bundle;
        if (options.bundle === "always" || options.bundle === "force-bundle") return BundleOptions.BundleOnly;
        return BundleOptions.Priority;
    })();

    if (IS_LOCAL_NX_DEV) {
        const source =
            batch.bundle !== undefined
                ? "batch"
                : options.bundle === "always" || options.bundle === "force-bundle"
                  ? "options"
                  : "default";
        // eslint-disable-next-line no-console
        console.debug("[txn-sender] resolveBundleOption", {
            resolved: BundleOptions[resolved],
            source,
            txCount: batch.transactions.length,
            identifiers: batch.transactions.map((t) => t.identifier)
        });
    }

    return resolved;
}

async function getIsGeyserConfirmed(signatures: string[], minCount: number) {
    try {
        const res = await axiosCreateMarkets().post<number>(TRANSACTIONS_STATUS_ROUTE, signatures);
        return typeof res.data === "number" && res.data >= minCount;
    } catch {
        return false;
    }
}

async function pollUntilGeyserConfirmed(signatures: string[], minCount: number, interval = 500, maxTime = 10000) {
    const startTime = Date.now();

    while (Date.now() - startTime < maxTime) {
        const isConfirmed = await getIsGeyserConfirmed(signatures, minCount);
        if (isConfirmed) {
            return true;
        }
        await sleep(interval);
    }
    return false;
}

export function useSignInTransactionAuth() {
    const sign = useSignTransactions();

    return async (
        transactionWalletAuth: TransactionWalletAuth,
        state: { authSlice: AuthSliceState }
    ): Promise<Result<string>> => {
        const headers = getTransactionHeadersFromCookies(state);

        if (!headers.isOk()) return Result.err(headers);

        if (!("wallet" in transactionWalletAuth)) return Result.errFromMessage("Not able to find a wallet to sign");

        const signInTransaction = await getSignInTransaction(
            headers.unwrap(),
            transactionWalletAuth.wallet.publicKey.toString()
        );
        if (!signInTransaction.isOk()) return Result.err(signInTransaction);

        const signedTransactionRes = await sign(
            [{ transactions: [{ transaction: signInTransaction.unwrap(), identifier: "Sign in" }] }],
            transactionWalletAuth,
            {}
        );
        if (!signedTransactionRes.isOk()) return Result.err(signedTransactionRes);
        const signedTransaction = signedTransactionRes.unwrap()[0].transactions[0].transaction;
        if (!signInTransaction) return Result.errFromMessage("Error signing transaction");
        const serialized = serializeTransactionForAuth(signedTransaction);
        return Result.ok(serialized);
    };
}

export function serializeTransactionForAuth(signedTransaction: SendableTransaction) {
    return base64.encode(signedTransaction.serialize({ requireAllSignatures: false }) as Buffer);
}
